import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { BackendAuthService } from './backend-auth.service';
import { SessionService } from './session.service';
import TiposDocumento from "@shared/enums/tipos-documento.enum";
import { ApiService } from "./api.service";
import { IResponseAPIListado } from "@models/responseAPI.interface";
import { assignToModel } from "@shared/utils/fieldsMap.utils";
import { Archivo } from "@models/archivo.model";
import { Usuario } from "@models/usuario.model";
import { DangerError } from "@shared/errors/custom.error";
import SiNo from "@shared/enums/si-no.enum";

@Injectable({
	providedIn: "root",
})
export class DocumentoService {
	constructor(
        private apiService: ApiService,
        private http: HttpClient,
        private authService: BackendAuthService,
        private sessionService: SessionService
    ) {}

	async obtenerDocumentos(
		isLicencia: 1 | 2,
		id: number,
		tipoDocumento?: TiposDocumento
	): Promise<{ data: Archivo[]; msg: string; success: boolean }> {
		const body: any = { isLicencia, id };
		if (tipoDocumento) body.tipoDocumento = tipoDocumento;
		const { data, msg, success } =
			await this.apiService.privatePost<IResponseAPIListado>(
				`fisca-dev-listar-documentos-licencia`,
				body
			);

		const archivos = data.map((l: any) => {
			l.tipo = {
				codigo: l.tdo_codigo,
				nombre: l.tdo_nombre,
				obligatorio: {
					codigo: l.syn_codigo_obligatorio,
					nombre: l.obligatorio,
				},
                requerido: l.syn_codigo_obligatorio
			};
			l.estado = {
				codigo: l.edo_codigo,
				nombre: l.edo_nombre,
			};
			l.usuario_creador = assignToModel(Usuario, l, {
				rut_usuario: "usu_rut_creador",
			});
			l.usuario_creador = assignToModel(Usuario, l, {
				rut_usuario: "usu_rut_actualizador",
			});
			l.s3 = {
				bucket: l.archivo.s3_bucket,
				key: l.archivo.s3_key,
				type: l.archivo.content_type,
				length: l.archivo.content_length,
				etag: l.archivo.etag,
				ultima_modificacion: l.archivo.last_modified,
				metadada: l.archivo.metadata,
				base64: l.archivo.base64,
			};
			return assignToModel(Archivo, l, {
				id: "lid_id",
				nombre: "lid_archivo",
				ruta: "lid_ruta",
				uuid: "lid_uuid",
				fecha_ingreso: "lid_fecha_ingreso",
				fecha_actualizacion: "lid_fecha_actualizacion",
			});
		});
		return { data: archivos, msg, success };
	}

    async subirDocumento(payload: {
        exp_id: number;
        usu_rut: number;
        tdo_codigo: number;
        doc_nombre_archivo: string;
        archivo: string; // Base64
    }): Promise<{ success: boolean; msg: string }> {
        try {
            const tokenRes = await this.authService.obtenerToken();
            const token = tokenRes.access_token;
            const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
            const url = `${environment.backendUrl}/esi-carga-documentos`;

            const response = await firstValueFrom(this.http.post<any>(url, payload, { headers }));
            
            if (response.success !== true && response.success !== 'true') {
                 throw new DangerError(response.msg || 'Error al subir documento');
            }

            return { success: true, msg: response.msg || 'Documento subido correctamente' };
        } catch (error) {
           console.error("Error al subir documento", error);
           throw error;
        }
    }

    async verDocumento(urlRelativa: string): Promise<Blob> {
        try {
            const tokenRes = await this.authService.obtenerToken();
            const token = tokenRes.access_token;
            const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'session_id': this.sessionService.getToken() || '' });
            
            // Ensure no double slash if backendUrl ends with / or urlRelativa starts with /
            // Adjust based on observation of environment.backendUrl (usually no trailing slash)
            // urlRelativa likely "dev/expedientes..."
            const baseUrl = environment.backendUrl.replace(/\/$/, ''); 
            const path = urlRelativa.startsWith('/') ? urlRelativa.substring(1) : urlRelativa;
            const url = `${baseUrl}/esi-files/${path}`;

            return await firstValueFrom(this.http.post(url, {}, { headers, responseType: 'blob' }));
        } catch (error) {
            console.error("Error al descargar documento", error);
            throw error;
        }
    }
}
